import { Badge } from '@up2stay/up2promo-ui';
import { AlertTriangle } from 'lucide-react';

import {
  isUnlimitedQuota,
  REPUTATION_LOCATION_ADDON_MONTHLY_EUR,
} from '@/core/authz/entitlement-catalog';
import { useFormatEur } from '@/core/billing/use-format-eur';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useQuota } from '@/core/hooks/use-quota';
import { useDictionary } from '@/core/providers/dictionary-provider';

type SeatUsageIndicatorProps = {
  /** `badge` = pill; `meta` = quiet muted line for identity strips. */
  variant?: 'badge' | 'meta';
};

export function SeatUsageIndicator({
  variant = 'badge',
}: SeatUsageIndicatorProps) {
  const dict = useDictionary();
  const usageDict = dict.entitlements.usage;
  const { usage, isLoading: entitlementsLoading } = useEntitlements();
  const { current, limit, isUnlimited, isOverLimit, isLoading } =
    useQuota('quota.members');
  const pending = usage?.members.pending ?? 0;

  if (entitlementsLoading || isLoading) return null;

  const pendingSuffix =
    pending > 0
      ? ` · ${usageDict.seatsPending.replace('{count}', String(pending))}`
      : '';

  if (isUnlimited) {
    const text = `${usageDict.seatsUnlimited.replace('{current}', String(current))}${pendingSuffix}`;
    if (variant === 'meta') {
      return (
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
          {text}
        </p>
      );
    }
    return <Badge variant="secondary">{text}</Badge>;
  }

  if (limit === undefined) return null;

  const label = usageDict.seatsUsed
    .replace('{current}', String(current))
    .replace('{limit}', String(limit));
  const text = `${label}${pendingSuffix}`;

  if (variant === 'meta') {
    return (
      <p
        className={`text-[13.5px] leading-relaxed ${
          isOverLimit ? 'text-destructive' : 'text-muted-foreground'
        }`}
      >
        {text}
      </p>
    );
  }

  return (
    <Badge variant={isOverLimit ? 'destructive' : 'secondary'}>
      {isOverLimit ? <AlertTriangle className="mr-1 h-3 w-3" /> : null}
      {text}
    </Badge>
  );
}

export function ReputationLocationUsageIndicator() {
  const dict = useDictionary();
  const usageDict = dict.entitlements.usage;
  const {
    isFeatureEnabled,
    usage,
    isLoading: entitlementsLoading,
  } = useEntitlements();

  if (entitlementsLoading) return null;
  if (!isFeatureEnabled('feature.reputation')) return null;

  const rep = usage?.reputationLocations;
  if (!rep) return null;

  const label = isUnlimitedQuota(rep.included)
    ? usageDict.reputationLocationsUnlimited.replace(
        '{connected}',
        String(rep.connected),
      )
    : usageDict.reputationLocationsIncluded
        .replace('{connected}', String(rep.connected))
        .replace('{included}', String(rep.included));

  return <Badge variant="secondary">{label}</Badge>;
}

interface ReputationConnectAllowanceProps {
  className?: string;
}

export function ReputationConnectAllowance({
  className,
}: ReputationConnectAllowanceProps) {
  const dict = useDictionary();
  const usageDict = dict.entitlements.usage;
  const { isFeatureEnabled, usage, isLoading } = useEntitlements();
  const formatEur = useFormatEur();

  if (isLoading) return null;
  if (!isFeatureEnabled('feature.reputation')) return null;

  const rep = usage?.reputationLocations;
  if (!rep) return null;

  const label = isUnlimitedQuota(rep.included)
    ? usageDict.reputationConnectUnlimited
    : rep.connected < rep.included
      ? usageDict.reputationConnectWithinIncluded
          .replace('{connected}', String(rep.connected))
          .replace('{included}', String(rep.included))
      : usageDict.reputationConnectBeyondIncluded.replace(
          '{price}',
          formatEur(REPUTATION_LOCATION_ADDON_MONTHLY_EUR),
        );

  return (
    <p className={`text-sm text-muted-foreground ${className ?? ''}`.trim()}>
      {label}
    </p>
  );
}
